import './index.css'

import React, { useState } from 'react'
import { useDispatch, useSelector } from 'hooks/use-redux'
import { useNavigate } from 'react-router-dom'
import api from '@disco/api/server'
import { WATERMARKING_LOAD_SUCCESS } from '@disco/constants/ActionTypes'
import { TRACK } from '@disco/constants/ItemTypes'
import { deleteItem } from '@disco/utils/immutable'
import isAudioFile from '@disco/utils/isAudioFile'
import { uploadFile } from '@disco/utils/upload'
import FileSelect from 'components/common/file-select'
import DialogFooter from 'components/modals/dialog-footer'
import Button from 'components/ui/button'
import DiscoModal from 'components/ui/disco-modal'
import FileDropWrapper from 'containers/FileDropWrapper'

const TrackLeakTracing = (): React.ReactNode => {
  const [uploadedTrack, setUploadedTrack] = useState(null)
  const [uploading, setUploading] = useState(false)
  const [submiting, setSubmiting] = useState(false)
  const [completed, setСompleted] = useState(false)
  const [error, setError] = useState(null)
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const leaks = useSelector(state => state.watermarking.leaks)

  const close = () => navigate('/watermarking/leak-tracing')

  const submit = async () => {
    if (uploadedTrack) {
      setSubmiting(true)

      try {
        const leakTrace = await api.postLeakTraces({
          s3_upload_path:
            uploadedTrack.uploadPolicy['Key'] ||
            uploadedTrack.uploadPolicy.fields.key,
          file_name: uploadedTrack.name,
        })
        const items = leaks.nextPage
          ? deleteItem(leaks.items, leaks.items.length - 1) // when we insert new item, we need to remove last one to not duplicate it as it will be added again after loading next page
          : leaks.items
        dispatch({
          type: WATERMARKING_LOAD_SUCCESS,
          result: {
            results: [leakTrace, ...items],
            next: leaks.nextPage,
          },
          itemType: TRACK,
        })
        setСompleted(true)
      } catch (error) {
        setError(error)
        console.log('# error', error)
      }

      setSubmiting(false)
    }
  }

  const onFileSelect = async files => {
    const file = files[0]
    if (!isAudioFile(file)) return
    setUploading(true)
    const { uploadPolicy } = await uploadFile(file, null, null, {}, null, {
      skipFinalization: true,
      skipDuplicatesCheck: true,
    })
    setUploadedTrack({
      name: file.name,
      uploadPolicy,
    })
    setUploading(false)
  }

  let fileSelectCaption = 'Drag files here, or click to browse'

  if (uploading) {
    fileSelectCaption = 'Uploading...'
  }

  if (uploadedTrack?.name) {
    fileSelectCaption = uploadedTrack?.name
  }

  if (error) {
    fileSelectCaption = error.body
  }

  return (
    <DiscoModal
      style={{
        overlay: {
          zIndex: 1000,
          backgroundColor: '#02120cb3',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
        },
      }}
      onRequestClose={close}
      className="flc"
      isOpen
      handle
    >
      <div className="TrackLeakTracing">
        <h2 className="TrackLeakTracing_Title">Trace a leaked track</h2>
        {completed ? (
          <>
            <p className="TrackLeakTracing_Description">
              Your leak trace has been submitted and should be completed
              shortly. You’ll be notified when it’s complete.
            </p>
            <div className="TrackLeakTracing_Footer">
              <DialogFooter
                primary={
                  <Button
                    appearance="primary"
                    caption="Close"
                    onClick={close}
                  />
                }
              />
            </div>
          </>
        ) : (
          <>
            <p className="TrackLeakTracing_Description">
              Here you can trace leaked files you sent using DISCO’s
              watermarking to see who they were originally shared with. To trace
              a file, upload it via the tool below.
            </p>

            <div className="e2r-sl-dropzone">
              <FileDropWrapper onDrop={onFileSelect}>
                <FileSelect
                  className="e2r-sl-dropzone-txt hidden-phone"
                  multiple
                  onChange={onFileSelect}
                >
                  {fileSelectCaption}
                </FileSelect>
              </FileDropWrapper>
            </div>

            <div className="TrackLeakTracing_Footer">
              <DialogFooter
                onClose={close}
                primary={
                  <Button
                    appearance="primary"
                    caption="Submit"
                    disabled={!uploadedTrack || submiting}
                    onClick={submit}
                  />
                }
              />
            </div>
          </>
        )}
      </div>
    </DiscoModal>
  )
}

export default TrackLeakTracing
